import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Plus, Users, Briefcase, CalendarCheck2 } from "lucide-react";
import { toast } from "sonner";

const OPENING_STATUSES = [
  { key: "open", label: "Open", color: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30" },
  { key: "interviewing", label: "Interviewing", color: "bg-blue-500/20 text-blue-300 border-blue-500/30" },
  { key: "on_hold", label: "On hold", color: "bg-amber-500/20 text-amber-300 border-amber-500/30" },
  { key: "closed", label: "Closed", color: "bg-slate-500/20 text-slate-400 border-slate-500/30" },
];
const CANDIDATE_STAGES = [
  { key: "applied", label: "Applied" },
  { key: "screening", label: "Screening" },
  { key: "interview_scheduled", label: "Interview Scheduled" },
  { key: "interview_done", label: "Interview Done" },
  { key: "offer", label: "Offer" },
  { key: "hired", label: "Hired" },
  { key: "rejected", label: "Rejected" },
];
const REVIEW_STATUSES = [
  { key: "scheduled", label: "Scheduled", color: "bg-blue-500/20 text-blue-300 border-blue-500/30" },
  { key: "done", label: "Done", color: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30" },
  { key: "skipped", label: "Skipped", color: "bg-slate-500/20 text-slate-400 border-slate-500/30" },
];
const RATINGS = ["exceeds", "meets", "needs_improvement", "unsatisfactory"];

const emptyOpening = { title: "", department: "", level: "", headcount: 1, status: "open", owner_id: "", description: "" };
const emptyCandidate = { opening_id: "", name: "", mobile: "", email: "", stage: "applied", notes: "" };
const emptyReview = { employee_id: "", reviewer_id: "", cycle: "", review_date: "", status: "scheduled",
                     rating: "", strengths: "", growth_areas: "", action_items: "", next_review_date: "" };

const badge = (arr, k) => arr.find((x) => x.key === k) || arr[0];

export default function HR() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [users, setUsers] = useState([]);
  const [openings, setOpenings] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [reviews, setReviews] = useState([]);

  const [openO, setOpenO] = useState(false);
  const [openC, setOpenC] = useState(false);
  const [openR, setOpenR] = useState(false);
  const [formO, setFormO] = useState(emptyOpening);
  const [formC, setFormC] = useState(emptyCandidate);
  const [formR, setFormR] = useState(emptyReview);
  const [editO, setEditO] = useState(null);
  const [editC, setEditC] = useState(null);
  const [editR, setEditR] = useState(null);

  const load = useCallback(async () => {
    const [o, c, r, u] = await Promise.all([
      api.get("/hr/openings"), api.get("/hr/candidates"),
      api.get("/hr/reviews"), api.get("/users"),
    ]);
    setOpenings(o.data); setCandidates(c.data); setReviews(r.data); setUsers(u.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const userName = (id) => users.find((u) => u.id === id)?.name || "—";
  const openingTitle = (id) => openings.find((o) => o.id === id)?.title || "—";

  const saveOpening = async () => {
    try {
      if (editO) await api.patch(`/hr/openings/${editO}`, formO);
      else await api.post("/hr/openings", formO);
      toast.success("Saved"); setOpenO(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const saveCandidate = async () => {
    try {
      if (editC) await api.patch(`/hr/candidates/${editC}`, formC);
      else await api.post("/hr/candidates", formC);
      toast.success("Saved"); setOpenC(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const saveReview = async () => {
    try {
      if (editR) await api.patch(`/hr/reviews/${editR}`, formR);
      else await api.post("/hr/reviews", formR);
      toast.success("Saved"); setOpenR(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const openCount = openings.filter((o) => o.status !== "closed").length;
  const activeCandidates = candidates.filter((c) => !["hired", "rejected"].includes(c.stage));
  const upcomingReviews = reviews.filter((r) => r.status === "scheduled");

  return (
    <div className="space-y-4" data-testid="hr-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">People operations</div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Human Resources</h1>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatTile icon={Briefcase} label="Open Positions" value={openCount} tone="brand" />
        <StatTile icon={Users} label="Active Candidates" value={activeCandidates.length} tone="primary" />
        <StatTile icon={CalendarCheck2} label="Reviews Scheduled" value={upcomingReviews.length} tone="warning" />
      </div>

      <Tabs defaultValue="hiring" className="w-full">
        <TabsList>
          <TabsTrigger value="hiring" data-testid="hr-tab-hiring"><Briefcase className="h-4 w-4 mr-2" />Hiring</TabsTrigger>
          <TabsTrigger value="reviews" data-testid="hr-tab-reviews"><CalendarCheck2 className="h-4 w-4 mr-2" />Performance Reviews</TabsTrigger>
        </TabsList>

        <TabsContent value="hiring" className="space-y-4">
          <Card className="bg-card border-border">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <h3 className="font-display font-semibold">Open Positions</h3>
              {isAdmin && <Button size="sm" onClick={() => { setEditO(null); setFormO(emptyOpening); setOpenO(true); }} data-testid="new-opening-btn"><Plus className="h-4 w-4 mr-2" />New Opening</Button>}
            </div>
            <Table>
              <TableHeader><TableRow>
                <TableHead>Title</TableHead><TableHead>Department</TableHead><TableHead>Level</TableHead>
                <TableHead>Hiring Owner</TableHead><TableHead>Headcount</TableHead><TableHead>Candidates</TableHead><TableHead>Status</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {openings.map((o) => {
                  const b = badge(OPENING_STATUSES, o.status);
                  const count = candidates.filter((c) => c.opening_id === o.id).length;
                  return (
                    <TableRow key={o.id} className="cursor-pointer" onClick={() => { setEditO(o.id); setFormO({ ...o }); setOpenO(true); }} data-testid={`opening-row-${o.id}`}>
                      <TableCell className="font-medium">{o.title}</TableCell>
                      <TableCell>{o.department || "—"}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{o.level || "—"}</TableCell>
                      <TableCell className="text-sm">{userName(o.owner_id)}</TableCell>
                      <TableCell className="font-mono">{o.headcount}</TableCell>
                      <TableCell className="font-mono">{count}</TableCell>
                      <TableCell><Badge className={`${b.color} border`}>{b.label}</Badge></TableCell>
                    </TableRow>
                  );
                })}
                {openings.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">No openings yet.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </Card>

          <Card className="bg-card border-border">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <h3 className="font-display font-semibold">Candidates</h3>
              <Button size="sm" onClick={() => { setEditC(null); setFormC(emptyCandidate); setOpenC(true); }} data-testid="new-candidate-btn"><Plus className="h-4 w-4 mr-2" />Add Candidate</Button>
            </div>
            <Table>
              <TableHeader><TableRow>
                <TableHead>Name</TableHead><TableHead>Opening</TableHead>
                <TableHead>Contact</TableHead><TableHead>Stage</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {candidates.map((c) => (
                  <TableRow key={c.id} className="cursor-pointer" onClick={() => { setEditC(c.id); setFormC({ ...c }); setOpenC(true); }} data-testid={`candidate-row-${c.id}`}>
                    <TableCell className="font-medium">{c.name}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{openingTitle(c.opening_id)}</TableCell>
                    <TableCell className="text-sm font-mono">{c.contact || "—"}</TableCell>
                    <TableCell><Badge variant="outline" className="capitalize">{c.stage.replace(/_/g, " ")}</Badge></TableCell>
                  </TableRow>
                ))}
                {candidates.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-8">No candidates yet.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="reviews" className="space-y-4">
          <Card className="bg-card border-border">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <h3 className="font-display font-semibold">Performance Reviews</h3>
              {isAdmin && <Button size="sm" onClick={() => { setEditR(null); setFormR({ ...emptyReview, reviewer_id: user?.id || "" }); setOpenR(true); }} data-testid="new-review-btn"><Plus className="h-4 w-4 mr-2" />Schedule Review</Button>}
            </div>
            <Table>
              <TableHeader><TableRow>
                <TableHead>Employee</TableHead><TableHead>Reviewer</TableHead>
                <TableHead>Cycle</TableHead><TableHead>Date</TableHead>
                <TableHead>Rating</TableHead><TableHead>Status</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {reviews.map((r) => {
                  const b = badge(REVIEW_STATUSES, r.status);
                  return (
                    <TableRow key={r.id} className="cursor-pointer" onClick={() => { setEditR(r.id); setFormR({ ...r }); setOpenR(true); }} data-testid={`review-row-${r.id}`}>
                      <TableCell className="font-medium">{userName(r.employee_id)}</TableCell>
                      <TableCell className="text-sm">{userName(r.reviewer_id)}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{r.cycle || "—"}</TableCell>
                      <TableCell className="font-mono text-xs">{r.review_date || "—"}</TableCell>
                      <TableCell className="text-sm capitalize">{r.rating?.replace(/_/g, " ") || "—"}</TableCell>
                      <TableCell><Badge className={`${b.color} border`}>{b.label}</Badge></TableCell>
                    </TableRow>
                  );
                })}
                {reviews.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">No reviews scheduled.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Opening dialog */}
      <Dialog open={openO} onOpenChange={setOpenO}>
        <DialogContent className="bg-card border-border max-w-lg">
          <DialogHeader><DialogTitle>{editO ? "Edit Opening" : "New Opening"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <HField label="Job Title" required>
              <Input placeholder="e.g. Installation Engineer" value={formO.title} onChange={(e) => setFormO({ ...formO, title: e.target.value })} data-testid="opening-title-input" />
            </HField>
            <div className="grid grid-cols-2 gap-3">
              <HField label="Department" hint="Sales, Design, Installation…">
                <Input placeholder="Installation" value={formO.department} onChange={(e) => setFormO({ ...formO, department: e.target.value })} />
              </HField>
              <HField label="Level" hint="Junior / Mid / Senior / Lead">
                <Input placeholder="Mid" value={formO.level} onChange={(e) => setFormO({ ...formO, level: e.target.value })} />
              </HField>
              <HField label="Headcount" hint="How many to hire?">
                <Input type="number" value={formO.headcount} onChange={(e) => setFormO({ ...formO, headcount: Number(e.target.value) })} />
              </HField>
              <HField label="Status">
                <Select value={formO.status} onValueChange={(v) => setFormO({ ...formO, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{OPENING_STATUSES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
                </Select>
              </HField>
            </div>
            <HField label="Hiring Owner" hint="Which team member is responsible for hiring this role?">
              <Select value={formO.owner_id || "none"} onValueChange={(v) => setFormO({ ...formO, owner_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="opening-owner-select"><SelectValue placeholder="Assign hiring lead" /></SelectTrigger>
                <SelectContent><SelectItem value="none">Unassigned</SelectItem>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
              </Select>
            </HField>
            <HField label="Job Description" hint="Role responsibilities, must-haves">
              <Textarea placeholder="…" value={formO.description || ""} onChange={(e) => setFormO({ ...formO, description: e.target.value })} />
            </HField>
          </div>
          <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setOpenO(false)}>Cancel</Button><Button onClick={saveOpening} data-testid="opening-save-btn">Save</Button></div>
        </DialogContent>
      </Dialog>

      {/* Candidate dialog */}
      <Dialog open={openC} onOpenChange={setOpenC}>
        <DialogContent className="bg-card border-border max-w-lg">
          <DialogHeader><DialogTitle>{editC ? "Edit Candidate" : "Add Candidate"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <HField label="Full Name" required>
              <Input placeholder="Aditya Menon" value={formC.name} onChange={(e) => setFormC({ ...formC, name: e.target.value })} data-testid="candidate-name-input" />
            </HField>
            <div className="grid grid-cols-2 gap-3">
              <HField label="Mobile" hint="Primary contact number">
                <Input placeholder="+91-…" value={formC.mobile || ""} onChange={(e) => setFormC({ ...formC, mobile: e.target.value })} data-testid="candidate-mobile-input" />
              </HField>
              <HField label="Email" hint="For sending offers, coordination">
                <Input type="email" placeholder="aditya@example.com" value={formC.email || ""} onChange={(e) => setFormC({ ...formC, email: e.target.value })} data-testid="candidate-email-input" />
              </HField>
            </div>
            <HField label="Applied For" hint="Which opening?">
              <Select value={formC.opening_id || "none"} onValueChange={(v) => setFormC({ ...formC, opening_id: v === "none" ? "" : v })}>
                <SelectTrigger><SelectValue placeholder="Opening" /></SelectTrigger>
                <SelectContent><SelectItem value="none">Unassigned</SelectItem>{openings.map((o) => <SelectItem key={o.id} value={o.id}>{o.title}</SelectItem>)}</SelectContent>
              </Select>
            </HField>
            <HField label="Hiring Stage">
              <Select value={formC.stage} onValueChange={(v) => setFormC({ ...formC, stage: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{CANDIDATE_STAGES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </HField>
            <HField label="Notes" hint="Interview notes, resume link">
              <Textarea value={formC.notes || ""} onChange={(e) => setFormC({ ...formC, notes: e.target.value })} />
            </HField>
          </div>
          <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setOpenC(false)}>Cancel</Button><Button onClick={saveCandidate} data-testid="candidate-save-btn">Save</Button></div>
        </DialogContent>
      </Dialog>

      {/* Review dialog */}
      <Dialog open={openR} onOpenChange={setOpenR}>
        <DialogContent className="bg-card border-border max-w-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editR ? "Edit Review" : "Schedule Performance Review"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <HField label="Employee (Reviewee)" required hint="The team member being reviewed">
                <Select value={formR.employee_id || "none"} onValueChange={(v) => setFormR({ ...formR, employee_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="review-employee-select"><SelectValue placeholder="Whose review is this?" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">—</SelectItem>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
                </Select>
              </HField>
              <HField label="Reviewer (Manager)" hint="Who is conducting the review">
                <Select value={formR.reviewer_id || "none"} onValueChange={(v) => setFormR({ ...formR, reviewer_id: v === "none" ? "" : v })}>
                  <SelectTrigger><SelectValue placeholder="Manager conducting the review" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">—</SelectItem>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
                </Select>
              </HField>
              <HField label="Cycle" hint="e.g. Q1 2026, H1 2026, Annual 2026">
                <Input placeholder="H1 2026" value={formR.cycle || ""} onChange={(e) => setFormR({ ...formR, cycle: e.target.value })} />
              </HField>
              <HField label="Review Date" hint="Scheduled 1-on-1">
                <Input type="date" value={formR.review_date || ""} onChange={(e) => setFormR({ ...formR, review_date: e.target.value })} />
              </HField>
              <HField label="Status">
                <Select value={formR.status} onValueChange={(v) => setFormR({ ...formR, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{REVIEW_STATUSES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
                </Select>
              </HField>
              <HField label="Rating" hint="Set after review">
                <Select value={formR.rating || "none"} onValueChange={(v) => setFormR({ ...formR, rating: v === "none" ? "" : v })}>
                  <SelectTrigger><SelectValue placeholder="Not set" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">Not set</SelectItem>{RATINGS.map((r) => <SelectItem key={r} value={r} className="capitalize">{r.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                </Select>
              </HField>
              <HField label="Next Review Date">
                <Input type="date" value={formR.next_review_date || ""} onChange={(e) => setFormR({ ...formR, next_review_date: e.target.value })} />
              </HField>
            </div>
            <HField label="Strengths" hint="What they excelled at">
              <Textarea value={formR.strengths || ""} onChange={(e) => setFormR({ ...formR, strengths: e.target.value })} />
            </HField>
            <HField label="Growth Areas" hint="What to work on next">
              <Textarea value={formR.growth_areas || ""} onChange={(e) => setFormR({ ...formR, growth_areas: e.target.value })} />
            </HField>
            <HField label="Action Items" hint="Concrete commitments for next cycle">
              <Textarea value={formR.action_items || ""} onChange={(e) => setFormR({ ...formR, action_items: e.target.value })} />
            </HField>
          </div>
          <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setOpenR(false)}>Cancel</Button><Button onClick={saveReview} data-testid="review-save-btn">Save</Button></div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function HField({ label, hint, required, children }) {
  return (
    <div>
      <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}{required && <span className="text-red-400 ml-0.5">*</span>}
      </label>
      <div className="mt-1">{children}</div>
      {hint && <div className="text-[10.5px] text-muted-foreground/70 mt-1">{hint}</div>}
    </div>
  );
}

function StatTile({ icon: Icon, label, value, tone }) {
  const tones = {
    brand: "bg-[hsl(var(--brand-yellow))] text-[hsl(218_65%_10%)]",
    primary: "bg-[hsl(var(--brand-blue))/0.15] text-white border border-[hsl(var(--brand-blue))/0.5]",
    warning: "bg-amber-500/10 text-amber-300 border border-amber-500/30",
  };
  return (
    <Card className="p-5 bg-card border-border">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">{label}</div>
          <div className="font-display text-3xl font-bold mt-2">{value}</div>
        </div>
        <div className={`h-9 w-9 rounded grid place-items-center ${tones[tone]}`}>
          <Icon className="h-4 w-4" />
        </div>
      </div>
    </Card>
  );
}
